import { FormBlockInstance, LocalizedText } from '../api-types';

export const SINGLE_SELECT_TYPE = 'single_select';
export const SINGLE_SELECT_TYPE_VERSION = 1;

/** Falls back to the server's own default so the field is never unbounded if config is thin. */
export const DEFAULT_OTHER_TEXT_MAX_LENGTH = 200;

export type SingleSelectOptionKind = 'CHOICE' | 'OTHER' | 'NOT_APPLICABLE';

export interface SingleSelectOption {
  key: string;
  kind: SingleSelectOptionKind;
  label: LocalizedText | null;
  description: LocalizedText | null;
}

export interface SingleSelectConfig {
  options: SingleSelectOption[];
  otherTextMaxLength: number;
  showDescriptions: boolean;
}

/** The answer shape pro-core's `single_select@1` validates. */
export interface SingleSelectAnswer {
  optionKey: string;
  otherText?: string | null;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asLocalizedText = (value: unknown): LocalizedText | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

const asKind = (value: unknown): SingleSelectOptionKind =>
  value === 'OTHER' || value === 'NOT_APPLICABLE' ? value : 'CHOICE';

/**
 * Read a stored config into the shape this renderer needs.
 *
 * Tolerant on purpose. The definition was normalized and validated by pro-core before it was ever
 * stored, so the fields are expected to be there — but a renderer that throws on a surprise takes
 * the whole form down for the respondent, and a question rendered with fewer options than intended
 * is still refused at submit. Being forgiving here costs nothing and removes a crash class.
 */
export const readSingleSelectConfig = (
  block: FormBlockInstance
): SingleSelectConfig => {
  const config = asRecord(block.config);
  const data = asRecord(config.data);
  const interaction = asRecord(config.interaction);
  const presentation = asRecord(config.presentation);
  const rawOptions = Array.isArray(data.options) ? data.options : [];

  const options: SingleSelectOption[] = rawOptions
    .map(asRecord)
    .filter(option => typeof option.key === 'string' && option.key.length > 0)
    .map(option => ({
      key: option.key as string,
      kind: asKind(option.kind),
      label: asLocalizedText(option.label),
      description: asLocalizedText(option.description)
    }));

  const maxLength =
    typeof interaction.otherTextMaxLength === 'number' &&
    interaction.otherTextMaxLength > 0
      ? interaction.otherTextMaxLength
      : DEFAULT_OTHER_TEXT_MAX_LENGTH;

  return {
    options,
    otherTextMaxLength: maxLength,
    showDescriptions: presentation.showDescriptions === true
  };
};

export const readSingleSelectAnswer = (
  answer: unknown
): SingleSelectAnswer | null => {
  const record = asRecord(answer);
  return typeof record.optionKey === 'string' && record.optionKey.length > 0
    ? {
        optionKey: record.optionKey,
        otherText:
          typeof record.otherText === 'string' ? record.otherText : null
      }
    : null;
};

/**
 * Answered means a choice was made — and, when that choice is "other", that something was actually
 * typed. Whitespace does not count, matching the server's own check, so Next never enables on an
 * answer the submit would then reject.
 */
export const isSingleSelectAnswered = (
  answer: unknown,
  options: SingleSelectOption[]
): boolean => {
  const parsed = readSingleSelectAnswer(answer);
  if (!parsed) {
    return false;
  }
  const chosen = options.find(option => option.key === parsed.optionKey);
  if (chosen?.kind === 'OTHER') {
    return Boolean(parsed.otherText && parsed.otherText.trim().length > 0);
  }
  return true;
};
